import React, { Component } from "react";
import TimePicker from "antd/lib/time-picker";
import styled from "styled-components";
import moment from "moment";
import _ from "lodash";
import juration from "juration";
import { observer } from "mobx-react";
import R from "ramda";
import Select from "react-select";

import { timeFormat } from "./providers/clock";
import { Divider, ShowWhen } from "../reusable/layout";
import * as Colors from "../reusable/colors";
import { Remove, Clone } from "../reusable/icons";
import { inputComponent, SelectInput } from "../properties/input";
import dbStore, {
  dbJS,
  flattenDB,
  updateCell,
  addEntity,
  removeEntity,
  createCell,
  Providers
} from "./store";
import { orderList } from "../reusable/utils";
import { Categories } from "./constants";
import CustomCategoryComponent from "./customs";

import "antd/lib/time-picker/style/css";
import "./time-picker.css";

const Table = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 5px;
  border-left: 1px solid ${Colors.separatorLight};
`;

const Row = styled.ul`
  display: flex;
`;

const Cell = styled.li`
  width: 140px;
  background-color: ${p => (p.header ? Colors.secondary : "transparent")};
  font-weight: ${p => (p.entity ? "bold" : "normal")};
  color: ${p => (p.disabled ? "#ccc" : "inherit")};
  padding: 14px;
  border: 1px solid ${Colors.separatorLight};
  border-left: 0px;
  border-top: ${p => !p.header && "0px"};
  img {
    max-width: 100px;
  }
`;

const GroupHeader = styled.div`
  margin-bottom: 15px;
  margin-left: 15px;
`;

const CellText = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TableWraper = styled.div`
  overflow-y: auto;
`;

const SpreadButton = styled.button`
  padding: 8px 10px;
  margin: 3px;
  background-color: ${Colors.primary};
  border: none;
  border-radius: 5px;
  font-size: 0.8em;
  cursor: pointer;
`;

const InputUI = styled.input`
  width: ${p => p.width || "100%"};
  padding: 5px;
  border-radius: 5px;
  background-color: ${Colors.primary};
  border: none;
  outline: none;
`;

const Tags = styled.ul`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
`;

const Tag = styled.li`
  padding: 5px;
  margin-right: 4px;
  border: 1px solid ${Colors.primary};
  margin-bottom: 4px;
  border-radius: 4px;
`;

const CategoryList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-around;
`;

const Category = styled.li`
  width: 150px;
  padding: 10px;
  background-color: ${p => p.active && Colors.primary};
  border: 1px solid ${Colors.separatorLight};
  border-radius: 4px;
  text-transform: capitalize;
  text-align: center;
  cursor: pointer;
`;

class ListInput extends Component {
  constructor(p) {
    super(p);
    this.state = {
      input: ""
    };
  }

  addInput = e => {
    this.props.onChange({
      ...this.props.value,
      [this.state.input]: this.props.value ? Object.keys(this.props.value).length + 1 : 1
    });
    this.setState({ input: "" });
  };

  moveTo = (item, index) => {
    const ordered = orderList(this.props.value);
    const reordered = R.insert(
      R.mathMod(index, ordered.length),
      item,
      ordered.filter(i => i !== item)
    );
    this.props.onChange(R.mergeAll(reordered.map((i, idx) => ({ [i]: idx }))));
  };

  render() {
    return (
      <div>
        <Tags>
          {orderList(this.props.value || {}).map((v, idx) => (
            <Tag onClick={() => this.moveTo(v, idx - 1)} key={v}>
              {v}
            </Tag>
          ))}
        </Tags>
        <Select
          value={this.state.input}
          onChange={e => this.setState({ input: e.target.value })}
          clearable={false}
          style={{
            width: "110px",
            backgroundColor: Colors.primary,
            height: "32px",
            color: Colors.Text,
            zIndex: 99
          }}
          options={this.props.entities.map(o => ({ value: o, label: o }))}
          onBlur={this.stopEdit}
        />
        <SpreadButton onClick={this.addInput}>Add</SpreadButton>
      </div>
    );
  }
}

const EntityCategory = {
  [Categories.Twitch]: [Providers.Twitch, "twitch.subs"],
  [Categories.Time]: [Providers.Clock, Providers.Countdown],
  [Categories.Poll]: [Providers.Strawpoll, "strawpoll.options"],
  [Categories.Twitter]: [Providers.Twitter, Providers.TwitterFeed, "twitterfeed.tweets"],
  [Categories.Donation]: [Providers.Streamlabs, "streamlabs.donations"]
};

const isInCategory = (cat, type) => {
  //if in custom category, check that the entity is not part of defined categories
  return cat === Categories.Custom
    ? !_.flatten(_.values(EntityCategory)).includes(type)
    : EntityCategory[cat].includes(type);
};

export default observer(
  class extends Component {
    constructor(p) {
      super(p);
      this.state = {
        editing: undefined,
        newInput: "",
        adding: undefined,
        addingName: "",
        finished: undefined,
        category: Categories.Twitch
      };
    }

    showCategory = cat => e => {
      this.setState({ category: cat });
    };

    isEditing = (name, p, idx) => this.cellPath(name, p, idx) === this.state.editing;

    onChangeCell = (id, prop) => val => {
      updateCell(id, prop, val);
      if (prop === "duration") {
        try {
          const j = juration.parse(val);

          updateCell(id, "startAt", moment().format(timeFormat));
          updateCell(
            id,
            "target",
            moment()
              .add(j, "s")
              .format(timeFormat)
          );
        } catch (err) {
          console.log("err", err);
        }
      }
      if (prop === "target") updateCell(id, "startAt", moment().format(timeFormat));
    };

    cellPath = (name, p, idx) => name + "." + p + idx;

    edit = (name, p, idx) => () => this.setState({ editing: this.cellPath(name, p, idx) });

    stopEdit = () => this.setState({ editing: undefined });

    addEntity = id => () => {
      addEntity(id);
      this.setState({ adding: id, newInput: "" });
    };

    prepareDuplicate = (name, properties) => {
      this.setState({ duplicating: { name: name, props: properties } });
    };

    addDuplicate = (newName, props) => {
      addEntity(newName, props.props);
      this.setState({ duplicating: undefined });
    };

    addNewProp = (id, name, type) => () => {
      this.setState({ finished: undefined, adding: undefined, addingName: "" });
      createCell(id, name, type);
    };

    listKeys = list => {
      return (
        dbStore.get(Object.keys(list)[0]) &&
        Object.keys(dbStore.get(Object.keys(list)[0]).props).join(", ")
      );
    };

    renderProp = (type, value) => {
      const v = value; //processTemplate(value, flattenSpreadsheet(this.props.spreadsheet));
      const cps = {
        image: _ =>
          v ? <img src={v} alt={v} height="50px" /> : <span>Double click to set image</span>,
        time: _ => <CellText>{value}</CellText>,
        list: _ => (
          <Tags title={(value && this.listKeys(value)) || ""}>
            {orderList(value || {})
              .slice(0, 3)
              .map(v => <Tag key={v}>{v}</Tag>)}
            {Object.keys(value || {}).length > 3 && <Tag key="extra">...</Tag>}
          </Tags>
        )
      };

      return (cps[type] && cps[type]()) || <CellText>{v}</CellText>;
    };

    textInput = (value, onChange) => (
      <InputUI
        type="text"
        defaultValue={value}
        onChange={e => onChange(e.target.value)}
        onBlur={this.stopEdit}
      />
    );

    numberInput = (value, onChange) => (
      <InputUI
        type="number"
        defaultValue={value}
        onChange={e => onChange(Number(e.target.value))}
        onBlur={this.stopEdit}
      />
    );

    linkInput = (value, onChange, entity) => {
      return (
        <SelectInput
          value={value}
          options={Object.keys(dbJS.get())
            .filter(k => k[0] !== "_" && k !== entity)
            .sort()}
          onChange={onChange}
        />
      );
    };

    timeInput = (value, onChange) => (
      <TimePicker
        className="override-picker"
        value={moment(value)}
        format={"HH:mm"}
        onChange={(c, t) => {
          c && onChange(c.format());
        }}
        onBlur={this.stopEdit}
      />
    );

    listInput = (value, onChange) => (
      <ListInput entities={dbStore.keys()} value={value} onChange={onChange} />
    );

    colorInput = (value, onChange) => {
      const C = inputComponent.color;
      return <C value={value} onChange={onChange} />;
    };

    checkBoxInput = (value, onChange) => {
      return (
        <input type="checkbox" onChange={e => onChange(e.target.checked)} defaultChecked={value} />
      );
    };

    inputs = type => {
      return (
        {
          link: this.linkInput,
          time: this.timeInput,
          list: this.listInput,
          number: this.numberInput,
          color: this.colorInput,
          checkbox: this.checkBoxInput
        }[type] || this.textInput
      );
    };

    renderInput = (id, prop, cell) => {
      // const cell = this.props.spreadsheet[id]["params"][prop];
      return this.inputs(cell.type)(cell.value, this.onChangeCell(id, prop), id);
    };

    renderCell = (id, prop, cell, idx) => {
      return (
        {
          image: this.isEditing(id, prop, idx)
            ? this.renderInput(id, prop, cell)
            : this.renderProp(cell.type, cell.value)
        }[cell.type] || this.renderInput(id, prop, cell)
      );
    };

    renderEntity = (name, properties) => {
      const propertyProps = _.omitBy(properties.props, p => p.hidden);
      return (
        <Table key={name}>
          <Row>
            <Cell header />
            {Object.keys(propertyProps).map(p => (
              <Cell header key={p}>
                {p.replace("_", " ")}
              </Cell>
            ))}
            <Cell header>
              {this.state.adding === name ? (
                this.state.finished ? (
                  this.state.addingName
                ) : (
                  <InputUI
                    type="text"
                    defaultValue=""
                    onChange={e => this.setState({ addingName: e.target.value })}
                    onBlur={e => e.target.value.length > 0 && this.setState({ finished: name })}
                  />
                )
              ) : (
                <SpreadButton onClick={() => this.setState({ adding: name })}>
                  Add column
                </SpreadButton>
              )}
            </Cell>
          </Row>
          <Row>
            {name !== "duplicating" ? (
              <Cell entity>
                {name}
                {!Object.values(Providers).includes(name) && (
                  <div>
                    <Divider h="15px" />
                    <span onClick={() => removeEntity(name)}>
                      {" "}
                      <Remove />
                    </span>
                    <span onClick={() => this.prepareDuplicate(name, properties)}>
                      {"  "}
                      <Clone />
                    </span>
                  </div>
                )}
              </Cell>
            ) : (
              <Cell entity>
                <InputUI
                  defaultValue=""
                  placeholder="type a new name"
                  onBlur={e => this.addDuplicate(e.target.value, this.state.duplicating.props)}
                />
              </Cell>
            )}
            {_.map(propertyProps, (v, p) => (
              <Cell key={p} onDoubleClick={!p.readOnly && this.edit(name, p, 0)}>
                {v.readOnly ? this.renderProp(v.type, v.value) : this.renderCell(name, p, v, 0)}
              </Cell>
            ))}
            <Cell>
              {this.state.adding === name &&
                this.state.addingName.length > 0 &&
                ["list", "image", "text", "number", "time", "link", "color", "checkbox"].map(
                  type => (
                    <SpreadButton
                      key={type}
                      onClick={this.addNewProp(name, this.state.addingName, type)}>
                      {type}
                    </SpreadButton>
                  )
                )}
            </Cell>
          </Row>
        </Table>
      );
    };

    renderGroup = (group, items) => {
      const sortedIds = flattenDB.get()[group].map(i => i[group + ".__id"]);
      const sortedItems = _.sortBy(items, i => sortedIds.indexOf(i.props.id.value));
      const propsKeys = Object.keys(items[0].props).filter(x => x !== "id");
      return (
        <div>
          <GroupHeader>{group}</GroupHeader>
          <Table>
            <Row>
              {propsKeys.map(p => (
                <Cell header key={p}>
                  {p.replace("_", " ")}
                </Cell>
              ))}
            </Row>
            {sortedItems.map(i => (
              <Row key={i.props.id.value}>
                {propsKeys.map(k => (
                  <Cell key={k}>{this.renderProp(i.props[k].type, i.props[k].value)}</Cell>
                ))}
              </Row>
            ))}
          </Table>
        </div>
      );
    };

    render() {
      return (
        <TableWraper>
          <Divider h="20px" />
          <CategoryList>
            {_.map(Categories, c => {
              return (
                <Category key={c} onClick={this.showCategory(c)} active={this.state.category === c}>
                  {c}
                </Category>
              );
            })}
          </CategoryList>
          <Divider h="40px" />
          {_.chain(dbJS.get())
            .pickBy(p => !p.hidden && isInCategory(this.state.category, p.type))
            .assign(this.state.duplicating ? { duplicating: this.state.duplicating.props } : {})
            .map((p, k) => (
              <div key={k}>
                {this.renderEntity(k, p)}
                <Divider h="40px" />
              </div>
            ))
            .value()}
          {_.chain(dbJS.get())
            .pickBy(v => v.group && isInCategory(this.state.category, v.group))
            .mapValues((v, k) => ({ ...v, props: { ...v.props, id: { type: "text", value: k } } }))
            .groupBy("group")
            .map((items, group) => (
              <div key={group}>
                {this.renderGroup(group, items)}
                <Divider h="40px" />
              </div>
            ))
            .value()}
          <CustomCategoryComponent cat={this.state.category} />
          <ShowWhen p={this.state.category === Categories.Custom}>
            <InputUI
              type="text"
              width="200px"
              placeholder="Add my own data"
              onChange={e => this.setState({ newInput: e.target.value })}
              value={this.state.newInput}
            />
          </ShowWhen>
          <ShowWhen p={this.state.newInput.length > 0}>
            <SpreadButton onClick={this.addEntity(this.state.newInput)}>Add</SpreadButton>
          </ShowWhen>
        </TableWraper>
      );
    }
  }
);



// WEBPACK FOOTER //
// ./src/spreadsheet/table.js